import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Cloud Functions 2nd Gen (Cloud Run) URL mapping - europe-west9
// These URLs are generated during deployment and are stable for the project
// Updated 2026-01-06 with correct Cloud Run URLs
const CLOUD_RUN_FUNCTIONS: Record<string, string> = {
  // Core image processing
  enhanceImage: 'https://enhanceimage-wk53vtdgya-od.a.run.app',
  changePerspective: 'https://changeperspective-wk53vtdgya-od.a.run.app',
  applyColorGrading: 'https://applycolorgrading-wk53vtdgya-od.a.run.app',
  analyzeImageWithGemini: 'https://analyzeimagewithgemini-wk53vtdgya-od.a.run.app',
  generateThumbnails: 'https://generatethumbnails-wk53vtdgya-od.a.run.app',
  optimizeLandingImages: 'https://optimizelandingimages-wk53vtdgya-od.a.run.app',
  
  // Storyboard
  generateStoryboard: 'https://generatestoryboard-wk53vtdgya-od.a.run.app',
  deleteStoryboard: 'https://deletestoryboard-wk53vtdgya-od.a.run.app',
  extractStoryboardFramesOnDemand: 'https://extractstoryboardframesondemand-wk53vtdgya-od.a.run.app',
  upscaleStoryboardFrame: 'https://upscalestoryboardframe-wk53vtdgya-od.a.run.app',
  
  // FAL API Keys management
  addFalApiKey: 'https://addfalapikey-wk53vtdgya-od.a.run.app',
  deleteFalApiKey: 'https://deletefalapikey-wk53vtdgya-od.a.run.app',
  updateFalApiKey: 'https://updatefalapikey-wk53vtdgya-od.a.run.app',
  listFalApiKeys: 'https://listfalapikeys-wk53vtdgya-od.a.run.app',
  getFalApiKeyUsageDetails: 'https://getfalapikeyusagedetails-wk53vtdgya-od.a.run.app',
  syncFalApiKeysUsage: 'https://syncfalapikeysusage-wk53vtdgya-od.a.run.app',
  migrateLegacyFalKey: 'https://migratelegacyfalkey-wk53vtdgya-od.a.run.app',
  migrateGenerationsToKey: 'https://migrategenerationstokey-wk53vtdgya-od.a.run.app',
  testFalApiEndpoints: 'https://testfalapiendpoints-wk53vtdgya-od.a.run.app',
  rotateEncryptionKey: 'https://rotateencryptionkey-wk53vtdgya-od.a.run.app',
  
  // Admin & pricing
  setAdmin: 'https://setadmin-wk53vtdgya-od.a.run.app',
  setAdminBySecret: 'https://setadminbysecret-wk53vtdgya-od.a.run.app',
  grantCredits: 'https://grantcredits-wk53vtdgya-od.a.run.app',
  recalculateGenerationCosts: 'https://recalculategenerationcosts-wk53vtdgya-od.a.run.app',
  listUsedModels: 'https://listusedmodels-wk53vtdgya-od.a.run.app',
  
  // Stripe & subscriptions
  stripeWebhook: 'https://stripewebhook-wk53vtdgya-od.a.run.app',
  createCheckoutSession: 'https://createcheckoutsession-wk53vtdgya-od.a.run.app',
  createPortalSession: 'https://createportalsession-wk53vtdgya-od.a.run.app',
  changeSubscriptionPlan: 'https://changesubscriptionplan-wk53vtdgya-od.a.run.app',
  
  // Storage & infrastructure
  applyBucketCors: 'https://applybucketcors-wk53vtdgya-od.a.run.app',
  createEuBucket: 'https://createeubucket-wk53vtdgya-od.a.run.app',
  
  // Migrations
  migrateDisableSafetyChecker: 'https://migratedisablesafetychecker-wk53vtdgya-od.a.run.app',
  migrateFixSubscriptionPriceAmount: 'https://migratefixsubscriptionpriceamount-wk53vtdgya-od.a.run.app',
};

export function getFirebaseFunctionUrl(functionName: string): string {
  // Use Cloud Run URLs for 2nd Gen functions
  const cloudRunUrl = CLOUD_RUN_FUNCTIONS[functionName];
  if (cloudRunUrl) {
    return cloudRunUrl;
  }
  
  // Fallback to old Cloud Functions 1st Gen URL format (for any legacy functions)
  const region = import.meta.env.VITE_FIREBASE_REGION || 'europe-west9';
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  if (!projectId) throw new Error('VITE_FIREBASE_PROJECT_ID is not set');
  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
}

export function computeUpscaleCredits(preset: 'original' | 'auto_2k' | 'auto_4k' | 'auto'): number {
  if (preset === 'original') return 0;
  if (preset === 'auto_2k' || preset === 'auto') return 1;
  if (preset === 'auto_4k') return 2;
  return 0;
}

function inferExtensionFromContentType(contentType: string | null): string {
  if (!contentType) return '';
  const map: Record<string, string> = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'image/bmp': '.bmp',
    'image/tiff': '.tif',
    'application/octet-stream': '',
  };
  return map[contentType.toLowerCase()] ?? '';
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // Try filename* (RFC 5987)
  const starMatch = header.match(/filename\*=(?:UTF-8''|)([^;]+)/i);
  if (starMatch && starMatch[1]) {
    try { return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, '')); } catch { /* ignore */ }
  }
  // Try plain filename="..."
  const nameMatch = header.match(/filename="?([^";]+)"?/i);
  if (nameMatch && nameMatch[1]) return nameMatch[1].trim();
  return null;
}

function parseFilenameFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const pathname = u.pathname;
    const seg = pathname.split('/').filter(Boolean).pop();
    if (!seg) return null;
    const clean = decodeURIComponent(seg);
    return clean.includes('.') ? clean : null;
  } catch {
    return null;
  }
}

export async function downloadFile(url: string, suggestedFileName?: string): Promise<void> {
  // Determine filename early
  const fileName = suggestedFileName || parseFilenameFromUrl(url) || 'download';
  
  // For Firebase Storage URLs, try no-cors fetch first (works for public files)
  // This bypasses CORS restrictions by not reading the response headers
  const isFirebaseStorage = url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com');
  
  try {
    // Try CORS fetch first (works if CORS is properly configured)
    const resp = await fetch(url, { 
      mode: 'cors',
      cache: 'no-cache'
    });
    
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();

    // Determine filename from response headers if available
    const cd = resp.headers.get('content-disposition');
    const ct = resp.headers.get('content-type');
    let finalFileName = suggestedFileName || parseFilenameFromContentDisposition(cd) || parseFilenameFromUrl(url) || 'download';
    const hasExt = /\.[a-z0-9]{2,6}$/i.test(finalFileName);
    if (!hasExt) {
      const ext = inferExtensionFromContentType(ct) || '.bin';
      finalFileName += ext;
    }

    triggerBlobDownload(blob, finalFileName);
    return;
  } catch (corsErr) {
    console.warn("CORS fetch failed, trying no-cors fallback", corsErr);
  }

  // Fallback: Use no-cors mode to get an opaque response
  // This works for public Firebase Storage URLs even without CORS headers
  if (isFirebaseStorage) {
    try {
      const resp = await fetch(url, { 
        mode: 'no-cors',
        cache: 'no-cache'
      });
      
      // no-cors returns opaque response, we can still get the blob
      const blob = await resp.blob();
      
      // Ensure filename has extension
      let finalFileName = fileName;
      if (!/\.[a-z0-9]{2,6}$/i.test(finalFileName)) {
        // Try to infer from URL or default to .png for images
        const urlExt = parseFilenameFromUrl(url);
        if (urlExt && /\.[a-z0-9]{2,6}$/i.test(urlExt)) {
          const ext = urlExt.match(/\.[a-z0-9]{2,6}$/i)?.[0] || '.png';
          finalFileName += ext;
        } else {
          finalFileName += '.png';
        }
      }

      triggerBlobDownload(blob, finalFileName);
      return;
    } catch (noCorsErr) {
      console.warn("no-cors fetch also failed", noCorsErr);
    }
  }

  // Last resort: Use hidden iframe to trigger download without leaving page
  // This works when the server sends Content-Disposition: attachment header
  console.warn("All fetch methods failed, using direct link fallback");
  
  let href = url;
  try {
    const u = new URL(url);
    if (u.hostname.includes('firebasestorage.googleapis.com')) {
      const params = u.searchParams;
      if (!params.has('alt')) params.set('alt', 'media');
      u.search = params.toString();
      href = u.toString();
    }
  } catch {}

  // Use anchor with download attribute - don't open new tab
  // The download attribute works for same-origin or CORS-enabled resources
  // For cross-origin without CORS, browser may ignore download attribute but won't navigate away
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  // Don't use target="_blank" - we want to stay on the same page
  // If download attribute is ignored, the browser will handle the file based on its type
  // For images, this might open in current tab, but we'll use a hidden iframe as backup
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 100);
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
    a.remove();
  }, 100);
}

/**
 * Generates a thumbnail URL from a Firebase Storage URL.
 * The thumbnail is generated by the generateThumbnails Cloud Function.
 * 
 * Pattern: original path -> thumbs/{original_path}_800x800.webp
 * Example: upscaled/abc123.png -> thumbs/upscaled/abc123_800x800.webp
 * 
 * @param originalUrl - The original Firebase Storage URL
 * @param size - The thumbnail size (default: 800x800 for better quality)
 * @returns The thumbnail URL, or the original URL if transformation fails
 */
export function getThumbnailUrl(originalUrl: string | undefined, size: '400x400' | '800x800' = '800x800'): string {
  if (!originalUrl) return '';
  
  // Only transform Firebase Storage URLs
  if (!originalUrl.includes('firebasestorage.googleapis.com')) {
    return originalUrl;
  }

  try {
    const url = new URL(originalUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
    if (!pathMatch) return originalUrl;
    
    // Decode the path (Firebase encodes it)
    const encodedPath = pathMatch[1];
    const originalPath = decodeURIComponent(encodedPath);
    
    // Skip if already a thumbnail
    if (originalPath.startsWith('thumbs/') || originalPath.includes('_400x400') || originalPath.includes('_800x800')) {
      return originalUrl;
    }
    
    // Extract filename and extension
    const lastSlash = originalPath.lastIndexOf('/');
    const dir = lastSlash >= 0 ? originalPath.substring(0, lastSlash) : '';
    const filename = lastSlash >= 0 ? originalPath.substring(lastSlash + 1) : originalPath;
    
    // Remove extension and add thumbnail suffix
    const dotIndex = filename.lastIndexOf('.');
    const nameWithoutExt = dotIndex >= 0 ? filename.substring(0, dotIndex) : filename;
    
    // Build thumbnail path: thumbs/{dir}/{name}_{size}.webp
    const thumbPath = dir 
      ? `thumbs/${dir}/${nameWithoutExt}_${size}.webp`
      : `thumbs/${nameWithoutExt}_${size}.webp`;
    
    // Encode the new path
    const encodedThumbPath = encodeURIComponent(thumbPath);
    
    // Reconstruct URL with new path
    // Note: We preserve the token if present for authenticated access
    const newUrl = new URL(originalUrl);
    newUrl.pathname = url.pathname.replace(encodedPath, encodedThumbPath);
    
    return newUrl.toString();
  } catch (e) {
    console.warn('Failed to generate thumbnail URL:', e);
    return originalUrl;
  }
}

// Cache for tracking which thumbnails exist
const thumbnailExistsCache = new Map<string, boolean>();

/**
 * Check if a thumbnail URL exists (with caching).
 * Returns the thumbnail URL if it exists, otherwise the original URL.
 */
export async function getThumbnailUrlWithFallback(
  originalUrl: string | undefined,
  size: '400x400' | '800x800' = '800x800'
): Promise<string> {
  if (!originalUrl) return '';
  
  const thumbUrl = getThumbnailUrl(originalUrl, size);
  
  // If no transformation happened, return original
  if (thumbUrl === originalUrl) return originalUrl;
  
  // Check cache first
  if (thumbnailExistsCache.has(thumbUrl)) {
    return thumbnailExistsCache.get(thumbUrl) ? thumbUrl : originalUrl;
  }
  
  // Check if thumbnail exists with a HEAD request
  try {
    const response = await fetch(thumbUrl, { method: 'HEAD', mode: 'cors' });
    const exists = response.ok;
    thumbnailExistsCache.set(thumbUrl, exists);
    return exists ? thumbUrl : originalUrl;
  } catch {
    // On error (CORS, network), assume thumbnail doesn't exist
    thumbnailExistsCache.set(thumbUrl, false);
    return originalUrl;
  }
}